import { ApiError, queryKeys, type VoiceTransferTarget } from '@mevaser/shared'
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useToast,
} from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { BellOff, Mic, MicOff, Pause, Phone, PhoneForwarded, PhoneOff, Play } from 'lucide-react'
import { useEffect, useState } from 'react'
import { api } from '../../lib/api.js'
import { formatCallerNumber, useCallerMatch } from './caller-name.js'
import { useSoftphone } from './softphone-provider.js'

const formatNumber = formatCallerNumber

/** Who is on the line: the saved customer's name (linking to their page) with
 *  the number beneath, or just the number when nobody saved owns it (t1400). */
function CallerLabel({
  raw,
  numberAsTitle = false,
}: {
  raw: string | null
  /** With no match, show the number in the larger title style (in-call). */
  numberAsTitle?: boolean
}) {
  const match = useCallerMatch(raw)
  if (!match) {
    return (
      <p
        className={
          numberAsTitle ? 'text-sm font-medium' : 'font-identifier text-xs text-foreground-muted'
        }
      >
        {formatNumber(raw)}
      </p>
    )
  }
  return (
    <>
      <p className="text-sm font-medium">
        <Link
          to="/customers/$customerId"
          params={{ customerId: match.id }}
          className="hover:underline"
        >
          {match.name}
        </Link>
      </p>
      <p className="font-identifier text-xs text-foreground-muted">{formatNumber(raw)}</p>
    </>
  )
}

/** Name if known, else the number — for the one-line "on hold" note. */
function CallerInline({ raw }: { raw: string | null }) {
  const match = useCallerMatch(raw)
  return <>{match ? match.name : formatNumber(raw)}</>
}

/** Elapsed call time, ticking once a second — reset whenever the call this
 *  bar is showing changes (a new call starts). */
function useElapsed(active: boolean) {
  const [seconds, setSeconds] = useState(0)
  useEffect(() => {
    if (!active) {
      setSeconds(0)
      return
    }
    const id = setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [active])
  const m = String(Math.floor(seconds / 60)).padStart(2, '0')
  const s = String(seconds % 60).padStart(2, '0')
  return `${m}:${s}`
}

/** Pick who the call goes to, and whether they hear who is calling first
 *  (t1228). The "announce" toggle keeps the menu open; choosing a person sends. */
function TransferMenu() {
  const { transfer } = useSoftphone()
  const toast = useToast()
  const [announce, setAnnounce] = useState(false)
  const { data: targets } = useQuery({
    queryKey: queryKeys.supportVoiceTransferTargets(),
    queryFn: () => api.get<VoiceTransferTarget[]>('/support/voice/transfer-targets'),
    staleTime: 60_000,
  })

  const send = (target: VoiceTransferTarget) => {
    transfer(target.id, announce).then(
      () => toast.success(`Transferred to ${target.label}`),
      (error) =>
        toast.error(
          'Could not transfer the call',
          error instanceof ApiError ? error.message : undefined,
        ),
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <PhoneForwarded className="size-3.5" aria-hidden="true" /> Transfer
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Transfer to</DropdownMenuLabel>
        <DropdownMenuCheckboxItem checked={announce} onCheckedChange={setAnnounce}>
          Announce the caller first
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {targets && targets.length > 0 ? (
          targets.map((target) => (
            <DropdownMenuItem key={target.id} onSelect={() => send(target)}>
              <Phone /> {target.label}
            </DropdownMenuItem>
          ))
        ) : (
          <DropdownMenuItem disabled>No one else to transfer to</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * A docked bar for the softphone's live state — ringing, or on a call.
 * Rendered once in AppShell alongside the provider; invisible (returns null)
 * the rest of the time, so it never competes for space on an ordinary page.
 */
export function SoftphoneCallBar() {
  const {
    state,
    remoteNumber,
    ringingFromOtherDevice,
    soundBlocked,
    muted,
    answer,
    reject,
    silence,
    hangUp,
    toggleMute,
    hold,
    resume,
    waiting,
    parkedNumber,
    answerWaiting,
    declineWaiting,
  } = useSoftphone()
  const toast = useToast()
  const onCallError = (title: string) => (error: unknown) =>
    toast.error(title, error instanceof Error ? error.message : undefined)
  const elapsed = useElapsed(state === 'active')

  if (state === 'idle' && !waiting) return null

  const onLiveCall = state === 'active'
  const answerWith = (mode: 'hold' | 'end') =>
    void answerWaiting(mode).catch(onCallError('Could not answer the waiting call'))

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 px-4 pb-4">
      {waiting ? (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-surface px-4 py-2.5 shadow-lg">
          <span className="relative flex size-2">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex size-2 rounded-full bg-primary" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium">
              {waiting.fromOtherDevice ? 'Call to' : 'Call waiting'}
            </p>
            <CallerLabel raw={waiting.remoteNumber} />
          </div>
          <Button variant="ghost" size="sm" onClick={declineWaiting}>
            <PhoneOff className="size-3.5" aria-hidden="true" /> Decline
          </Button>
          {onLiveCall ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => answerWith('end')}>
                End &amp; answer
              </Button>
              {parkedNumber ? null : (
                <Button variant="primary" size="sm" onClick={() => answerWith('hold')}>
                  <Pause className="size-3.5" aria-hidden="true" /> Hold &amp; answer
                </Button>
              )}
            </>
          ) : (
            <Button variant="primary" size="sm" onClick={() => answerWith('hold')}>
              <Phone className="size-3.5" aria-hidden="true" /> Answer
            </Button>
          )}
        </div>
      ) : null}
      {state === 'idle' ? null : (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-surface px-4 py-2.5 shadow-lg">
          {state === 'ringing' ? (
            <>
              <span className="relative flex size-2">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex size-2 rounded-full bg-primary" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {ringingFromOtherDevice ? 'Call to' : 'Incoming call'}
                </p>
                <CallerLabel raw={remoteNumber} />
                {ringingFromOtherDevice ? (
                  <p className="text-xs text-foreground-muted">
                    Started on another of your devices
                  </p>
                ) : null}
                {soundBlocked && !ringingFromOtherDevice ? (
                  <p className="text-xs text-foreground-muted">
                    Sound is blocked on this browser. Tap anywhere on the page to hear calls ring.
                  </p>
                ) : null}
              </div>
              {ringingFromOtherDevice ? null : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    silence()
                    toast.success(
                      'Stopped ringing on this browser',
                      'Use the phone icon at the top to turn it off for good.',
                    )
                  }}
                >
                  <BellOff className="size-3.5" aria-hidden="true" /> Not here
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={reject}>
                <PhoneOff className="size-3.5" aria-hidden="true" /> Decline
              </Button>
              <Button variant="primary" size="sm" onClick={answer}>
                <Phone className="size-3.5" aria-hidden="true" /> Answer
              </Button>
            </>
          ) : (
            <>
              <div className="min-w-0">
                {state === 'connecting' ? (
                  <p className="text-sm font-medium">Calling…</p>
                ) : (
                  <CallerLabel raw={remoteNumber} numberAsTitle />
                )}
                {state === 'active' ? (
                  <p className="font-identifier text-xs text-foreground-muted tabular">{elapsed}</p>
                ) : null}
                {state === 'held' ? (
                  <p className="text-xs text-foreground-muted">Caller on hold</p>
                ) : null}
                {state === 'active' && parkedNumber ? (
                  <p className="text-xs text-foreground-muted">
                    <CallerInline raw={parkedNumber} /> on hold
                  </p>
                ) : null}
              </div>
              {state === 'active' ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={muted ? 'Unmute' : 'Mute'}
                    onClick={toggleMute}
                  >
                    {muted ? <MicOff /> : <Mic />}
                  </Button>
                  {parkedNumber ? null : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        void hold().catch(onCallError('Could not put the caller on hold'))
                      }
                    >
                      <Pause className="size-3.5" aria-hidden="true" /> Hold
                    </Button>
                  )}
                  <TransferMenu />
                </>
              ) : null}
              {state === 'held' ? (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => void resume().catch(onCallError('Could not resume the call'))}
                  >
                    <Play className="size-3.5" aria-hidden="true" /> Resume
                  </Button>
                  <TransferMenu />
                </>
              ) : null}
              <Button variant="danger" size="sm" onClick={hangUp}>
                <PhoneOff className="size-3.5" aria-hidden="true" /> Hang up
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
